/**
 * React Query for GET account/device-access-settings (shared cache).
 * Queries run only when `account_settings.pins_enabled` is true (except the lock-overlay locked branch).
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getDeviceAccessSettings, getDeviceAccessSettingsForUnlock } from "@common/lib/api/deviceAccess";
import type { DeviceAccessSettings } from "@common/utils/types";
import { useAccountContext } from "@/state/providers/AccountProvider";

export function deviceAccessSettingsQueryKey(accountId: string | undefined) {
    return ["device-access-settings", accountId ?? ""] as const;
}

const DEFAULT_STALE_MS = 2 * 60 * 1000;
const DEFAULT_GC_MS = 10 * 60 * 1000;

type UseDeviceAccessSettingsOptions = {
    enabled?: boolean;
    staleTime?: number;
};

export function useDeviceAccessSettings(
    options?: UseDeviceAccessSettingsOptions,
): UseQueryResult<DeviceAccessSettings, Error> {
    const { accountData } = useAccountContext();
    const accountId = accountData?.id;
    const pinsEnabled = accountData?.pins_enabled === true;
    const { enabled = true, staleTime = DEFAULT_STALE_MS } = options ?? {};
    return useQuery({
        queryKey: deviceAccessSettingsQueryKey(accountId),
        queryFn: () => getDeviceAccessSettings(accountId!),
        enabled: Boolean(accountId) && pinsEnabled && enabled,
        staleTime,
        gcTime: DEFAULT_GC_MS,
    });
}

/**
 * Lock overlay: while unlocked, same data as `useDeviceAccessSettings` but a distinct cache key.
 * While locked (session may be gone), fetches via account_id + device_id without auth.
 * When unlocked, skips GET unless `account_settings.pins_enabled` (same as `useDeviceAccessSettings`).
 * When locked, always fetches when device credentials exist — session is cleared on lock so `pins_enabled`
 * is not available from account context.
 */
export function useLockScreenDeviceAccessSettings(params: {
    isLocked: boolean;
    /** Device association account id — used for unauthenticated GET while locked */
    lockAccountId: string | undefined;
    /** Session account id — used for authenticated GET while unlocked */
    sessionAccountId: string | undefined;
    deviceId: string;
}): UseQueryResult<DeviceAccessSettings, Error> {
    const { isLocked, lockAccountId, sessionAccountId, deviceId } = params;
    const { accountData } = useAccountContext();
    const pinsEnabled = accountData?.pins_enabled === true;
    return useQuery({
        queryKey: ["device-access-settings", "lock-overlay", isLocked, lockAccountId, sessionAccountId, deviceId],
        queryFn: async () => {
            if (isLocked && lockAccountId && deviceId) {
                return getDeviceAccessSettingsForUnlock(lockAccountId, deviceId);
            }
            if (!sessionAccountId) {
                throw new Error("Device access settings: missing account");
            }
            return getDeviceAccessSettings(sessionAccountId);
        },
        enabled: isLocked ? Boolean(lockAccountId && deviceId) : Boolean(sessionAccountId) && pinsEnabled,
        staleTime: DEFAULT_STALE_MS,
        gcTime: DEFAULT_GC_MS,
        retry: (failureCount) => {
            if (isLocked) return false;
            return failureCount < 3;
        },
    });
}
